import React from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Card,
  Box,
  Typography,
  Grid,
  Stack,
} from "@mui/material";
import {
  Mail,
  Send,
  Users,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { CommonButton } from "../../../../Components/ui";
import { Path } from "../../../../../static/Constants";

const SUPPORT_CHANNELS = [
  {
    icon: Mail,
    iconColor: colors.primary.main,
    iconBg: colors.blue[50],
    title: "Official Email Support",
    desc: "Have custom questions, partnership inquiries, or need account assistance? Email our core developer & support team directly.",
    actionLabel: "Send Email",
    actionHref: "mailto:info@autobetlottery.com",
    external: true,
  },
  {
    icon: Users,
    iconColor: colors.purple[600],
    iconBg: colors.purple[50],
    title: "Creator Partnership Program",
    desc: "Launch your own branded community lotteries and raise funds with zero upfront software costs and 10% platform fee.",
    actionLabel: "Sign Up as Creator",
    actionPath: Path.registerasorg,
    external: false,
  },
  {
    icon: ShieldCheck,
    iconColor: colors.emerald[600],
    iconBg: colors.emerald[50],
    title: "Provably Fair Verifications",
    desc: "Every draw is powered by Chainlink VRF and immutable on-chain smart contracts. Verify code and game results on-chain.",
    actionLabel: "View Results",
    actionPath: Path.result,
    external: false,
  },
];

const HelpContactCard = () => {
  const history = useHistory();

  return (
    <Box id="contact-section" sx={{ mb: 4 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: borderRadiusPx.lg,
            bgcolor: colors.purple[50],
            color: colors.purple[600],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Mail size={20} />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "1.35rem" }}>
            Still Need Assistance?
          </Typography>
          <Typography variant="body2" sx={{ color: colors.slate[500], fontSize: "0.875rem" }}>
            Our team and global community are here to help you 24/7
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={3}>
        {SUPPORT_CHANNELS.map((item, idx) => {
          const IconComp = item.icon;
          return (
            <Grid item xs={12} md={4} key={idx}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: borderRadiusPx.xl,
                  backgroundColor: colors.dashboard.cardBg,
                  border: `1px solid ${colors.dashboard.cardBorder}`,
                  boxShadow: customShadows.cardFlat,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: colors.primary.main,
                    boxShadow: customShadows.cardHover,
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: borderRadiusPx.lg,
                      bgcolor: item.iconBg,
                      color: item.iconColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <IconComp size={22} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: colors.slate[900], mb: 1, fontSize: "1.05rem" }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.slate[600], lineHeight: 1.6, mb: 3 }}>
                    {item.desc}
                  </Typography>
                </Box>

                {item.external ? (
                  <CommonButton
                    variant="outline"
                    size="small"
                    component="a"
                    href={item.actionHref}
                    endIcon={<Send size={15} />}
                    sx={{ width: "100%" }}
                  >
                    {item.actionLabel}
                  </CommonButton>
                ) : (
                  <CommonButton
                    variant="outline"
                    size="small"
                    component={Link}
                    to={item.actionPath}
                    onClick={() => history.push(item.actionPath)}
                    endIcon={<ExternalLink size={15} />}
                    sx={{
                      width: "100%",
                      textDecoration: "none",
                    }}
                  >
                    {item.actionLabel}
                  </CommonButton>
                )}
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default HelpContactCard;
